import {useCallback} from 'react'
import {View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'
import {useIsFetching, useQueryClient} from '@tanstack/react-query'

import {useSetHomeBadge} from '#/state/home-badge'
import {SYSTEM_FOR_YOU_FEED_INFO} from '#/state/queries/feed'
import {
  type FeedDescriptor,
  RQKEY as FEED_RQKEY,
} from '#/state/queries/post-feed'
import {truncateAndInvalidate} from '#/state/queries/util'
import {CenteredView} from '#/view/com/util/Views'
import {atoms as a} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {useHeaderOffset} from '#/components/hooks/useHeaderOffset'
import {ArrowRotateCounterClockwise_Stroke2_Corner0_Rounded as RetryIcon} from '#/components/icons/ArrowRotate'
import {Text} from '#/components/Typography'

/*
 * The ranked feed's own empty state.
 *
 * A first-party ranked feed is not empty for the reason a followed feed is:
 * there is nobody to go and follow. It is empty because the ranking has nothing
 * eligible for this reader — which on a fresh deployment means the ranking state
 * has not been built yet, and on a healthy one means they have already been shown
 * everything the ranking is willing to serve.
 *
 * Saying "you may need to follow more users" here was wrong twice over: it
 * describes a different feed, and it sends the reader looking for accounts to
 * follow to fix a problem that following cannot fix. So this state says what is
 * true, and the one useful action — ask again — is a button, because a feed with
 * nothing now may have something in a minute.
 */

/** The descriptor of the system For-you page — the feed this state belongs to. */
const FOR_YOU_DESCRIPTOR: FeedDescriptor =
  SYSTEM_FOR_YOU_FEED_INFO.feedDescriptor

export function ForYouEmptyState() {
  const {_} = useLingui()
  const headerOffset = useHeaderOffset()
  const queryClient = useQueryClient()
  const setHomeBadge = useSetHomeBadge()
  /*
   * Whether the feed the button is retrying is in flight.
   *
   * Read from the query cache and not held in local state: the request this
   * button starts is the feed's own, so the button is busy exactly while the
   * feed is — no timer guessing when it is over, and no way to press it twice.
   */
  const isRetrying =
    useIsFetching({queryKey: FEED_RQKEY(FOR_YOU_DESCRIPTOR)}) > 0

  const onPressRetry = useCallback(() => {
    setHomeBadge(false)
    truncateAndInvalidate(queryClient, FEED_RQKEY(FOR_YOU_DESCRIPTOR))
  }, [queryClient, setHomeBadge])

  return (
    <CenteredView sideBorders style={[a.h_full_vh]}>
      <View
        style={[
          a.align_center,
          a.h_full_vh,
          a.py_3xl,
          a.px_xl,
          {
            paddingTop: headerOffset + a.py_3xl.paddingTop,
          },
        ]}>
        <View style={[a.align_center, a.gap_sm, a.pb_xl]}>
          <Text style={[a.text_xl, a.font_semi_bold]}>
            <Trans>Nothing to show here yet</Trans>
          </Text>
          <Text
            style={[a.text_md, a.text_center, a.leading_snug, {maxWidth: 340}]}>
            <Trans>
              For You is built from posts the ranking has classified. There is
              nothing eligible for you right now — it fills up as posts are
              ranked.
            </Trans>
          </Text>
        </View>

        <View style={[a.flex_row, a.gap_md, a.justify_center, a.flex_wrap]}>
          <Button
            disabled={isRetrying}
            label={_(msg`Try again`)}
            size="large"
            variant="solid"
            color="primary"
            onPress={onPressRetry}>
            <ButtonIcon icon={RetryIcon} position="left" />
            <ButtonText>{_(msg`Try again`)}</ButtonText>
          </Button>
        </View>
      </View>
    </CenteredView>
  )
}
